import React, { useEffect, useState, useCallback, useRef } from 'react'
import notification from 'antd/lib/notification'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { appVersion } from '../../services/api'

const WEB_HOST = process.env.REACT_APP_WEB_HOST
const NOTICE_KEY = 'new-version-notice'
const ACK_KEY = 'versionAcknowledged' // stores last version the user dismissed

function NewVersion () {
  // Track currentVersion in state and keep it in sync with localStorage
  const [currentVersion, setCurrentVersion] = useState(() => {
    // eslint-disable-next-line no-undef
    return localStorage.getItem('currentVersion')
  })
  // eslint-disable-next-line no-undef
  const [, setNewVersion] = useState(null)
  const [, setNewVersionDate] = useState(null)
  const [notificationApi, notificationContextHolder] = notification.useNotification()
  const navigate = useNavigate()
  const location = useLocation()
  const hasOpenedRef = useRef(false) // prevents duplicate opens within a render cycle

  // Function to check if version change is significant (major or minor, not patch)
  const isSignificantVersionChange = useCallback((currentVer, newVer) => {
    if (!currentVer || !newVer) return true

    // For development (hash versions), always show notification if different
    if (!currentVer.startsWith('v') || !newVer.startsWith('v')) {
      return currentVer !== newVer
    }

    const parseVersion = (version) => {
      const cleaned = version.replace('v', '')
      const parts = cleaned.split('.').map(Number)
      return {
        major: parts[0] || 0,
        minor: parts[1] || 0,
        patch: parts[2] || 0
      }
    }

    const current = parseVersion(currentVer)
    const newer = parseVersion(newVer)

    // Show notification only if major or minor version changed
    return current.major !== newer.major || current.minor !== newer.minor
  }, [])

  const markAcknowledged = useCallback((version) => {
    try {
      // eslint-disable-next-line no-undef
      localStorage.setItem(ACK_KEY, version)
    } catch {}
    // Ensure the notice is closed
    notificationApi.destroy(NOTICE_KEY)
    // allow future notices for different versions
    hasOpenedRef.current = false
  }, [notificationApi])

  const getVersion = useCallback(async () => {
    try {
      const server = await appVersion.getCurrentVersion()
      const serverVersion = server.version
      const serverDate = server.date

      // Keep local storage and state in sync for the current deployed version
      if (serverVersion !== currentVersion) {
        setNewVersion(serverVersion)
        setNewVersionDate(serverDate)
        updateVersion({ version: serverVersion, date: serverDate }, false)
        setCurrentVersion(serverVersion)
      }

      // Don't show on pricing page
      if (location.pathname === '/pricing') return

      // Respect acknowledgment: if user dismissed this exact version, don't show again
      // eslint-disable-next-line no-undef
      const acknowledged = localStorage.getItem(ACK_KEY)
      if (acknowledged === serverVersion) return

      // Only show for significant changes
      if (!isSignificantVersionChange(currentVersion, serverVersion)) return

      // Prevent multiple opens in quick succession
      if (hasOpenedRef.current) return
      hasOpenedRef.current = true

      notificationApi.open({
        key: NOTICE_KEY,
        placement: 'topRight',
        duration: 5,
        message: 'New Features & Improvements!',
        description: (
          <span>
            We&apos;ve made some exciting updates to enhance your experience. Tap here to explore what&apos;s new!<br />
            <Link style={{ color: '#1D6EE3' }} to={`${WEB_HOST}/releases`} target='_blank' rel='noreferrer'>Learn more</Link>
          </span>
        ),
        onClose: () => markAcknowledged(serverVersion),
        onClick: () => markAcknowledged(serverVersion)
      })
    } catch (error) {
      console.warn('cant fetch version', error)
    }
  }, [currentVersion, isSignificantVersionChange, location.pathname, markAcknowledged, notificationApi])

  const updateVersion = useCallback((server, closable = true) => {
    try {
      // eslint-disable-next-line no-undef
      localStorage.setItem('currentVersion', server.version)
      // eslint-disable-next-line no-undef
      localStorage.setItem('currentVersionDate', server.date)
    } catch {}
    if (closable) {
      notificationApi.destroy()
      navigate(0)
    }
  }, [notificationApi, navigate])

  useEffect(() => {
    getVersion()
  }, [getVersion])

  return (
    <>
      {notificationContextHolder}
    </>
  )
}

export default NewVersion
